import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  ArrowLeft, Mail, Phone, MapPin, Globe, Tag, Calendar,
  Flame, Thermometer, Snowflake, Clock, Star, Edit3, Save,
  PhoneCall, MessageSquare, Send, Activity, FileText,
  ChevronRight, AlertCircle, CheckCircle, User, DollarSign,
  X, Plus, ExternalLink, Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { leadsAPI, emailAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import RegisterLeadModal from '../../components/sales/RegisterLeadModal';
import LeadEditDrawer from '../../components/sales/LeadEditDrawer';

const STAGE_LABELS = {
  new_lead: 'New Lead',
  in_discussion: 'In Discussion',
  not_interested: 'Not Interested',
  prospective: 'Prospective',
  payment_received: 'Payment Received',
  registered: 'Registered Client',
};

const STAGE_COLORS = {
  new_lead: 'bg-blue-100 text-blue-700',
  in_discussion: 'bg-violet-100 text-violet-700',
  not_interested: 'bg-slate-100 text-slate-600',
  prospective: 'bg-amber-100 text-amber-700',
  payment_received: 'bg-emerald-100 text-emerald-700',
  registered: 'bg-teal-100 text-teal-700',
};

const PRIORITY_CONFIG = {
  hot: { icon: Flame, color: 'text-rose-500', badge: 'bg-rose-100 text-rose-700', label: 'Hot' },
  warm: { icon: Thermometer, color: 'text-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Warm' },
  cold: { icon: Snowflake, color: 'text-sky-500', badge: 'bg-sky-100 text-sky-700', label: 'Cold' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function ScoreBar({ score }) {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-slate-300';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-500">
        <span>Lead Score</span>
        <span className="font-semibold text-slate-700">{score}/100</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700 rounded-full`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function ActivityItem({ activity }) {
  const icons = {
    stage_change: <ArrowLeft className="w-3.5 h-3.5 rotate-180 text-violet-500" />,
    note_added: <MessageSquare className="w-3.5 h-3.5 text-blue-500" />,
    email_sent: <Mail className="w-3.5 h-3.5 text-sky-500" />,
    call_logged: <PhoneCall className="w-3.5 h-3.5 text-green-500" />,
    follow_up_set: <Calendar className="w-3.5 h-3.5 text-amber-500" />,
    field_updated: <Edit3 className="w-3.5 h-3.5 text-slate-400" />,
    assigned: <User className="w-3.5 h-3.5 text-indigo-500" />,
    converted: <CheckCircle className="w-3.5 h-3.5 text-teal-500" />,
    created: <Plus className="w-3.5 h-3.5 text-emerald-500" />,
  };
  return (
    <div className="flex gap-3 py-2">
      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icons[activity.activity_type] || <Activity className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{activity.description}</p>
        {activity.old_value && activity.new_value && (
          <p className="text-xs text-slate-400">
            {activity.old_value} → {activity.new_value}
          </p>
        )}
        <p className="text-xs text-slate-400 mt-0.5">
          {activity.user_name} · {new Date(activity.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

function CallLogForm({ leadId, onSaved }) {
  const [form, setForm] = useState({
    duration_minutes: '',
    outcome: 'follow_up',
    notes: '',
    next_follow_up: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await leadsAPI.logCall(leadId, {
        ...form,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      });
      toast.success('Call logged');
      setForm({ duration_minutes: '', outcome: 'follow_up', notes: '', next_follow_up: '' });
      onSaved();
    } catch {
      toast.error('Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-3 border">
      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
        <PhoneCall className="w-4 h-4 text-green-500" /> Log a Call
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Duration (minutes)</Label>
          <Input
            type="number"
            placeholder="e.g. 15"
            value={form.duration_minutes}
            onChange={(e) => set('duration_minutes', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Outcome</Label>
          <Select value={form.outcome} onValueChange={(v) => set('outcome', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="interested">Interested</SelectItem>
              <SelectItem value="not_interested">Not Interested</SelectItem>
              <SelectItem value="follow_up">Follow-up Required</SelectItem>
              <SelectItem value="no_answer">No Answer</SelectItem>
              <SelectItem value="call_back">Requested Callback</SelectItem>
              <SelectItem value="left_voicemail">Left Voicemail</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Notes</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="What was discussed..." />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Schedule Follow-up</Label>
          <Input type="datetime-local" value={form.next_follow_up} onChange={(e) => set('next_follow_up', e.target.value)} />
        </div>
      </div>
      <Button size="sm" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
        {saving ? 'Saving...' : 'Save Call'}
      </Button>
    </div>
  );
}

function NoteForm({ leadId, onSaved }) {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await leadsAPI.addNote(leadId, content);
      toast.success('Note added');
      setContent('');
      onSaved();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Textarea
        rows={3}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Add a note about this lead..."
      />
      <Button size="sm" onClick={handleSave} disabled={saving || !content.trim()}
        className="bg-blue-600 hover:bg-blue-700 gap-1.5">
        <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Add Note'}
      </Button>
    </div>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'overview';
  const { user } = useAuth();

  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [calls, setCalls] = useState([]);
  const [notes, setNotes] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [movingStage, setMovingStage] = useState('');

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await leadsAPI.getLead(leadId);
      setLead(data.lead);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Lead not found');
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  const fetchSubData = useCallback(async () => {
    const [actRes, callRes, noteRes, emailRes] = await Promise.allSettled([
      leadsAPI.getActivities(leadId),
      leadsAPI.getCalls(leadId),
      leadsAPI.getNotes(leadId),
      leadsAPI.getEmails(leadId),
    ]);
    if (actRes.status === 'fulfilled') setActivities(actRes.value.data.activities || []);
    if (callRes.status === 'fulfilled') setCalls(callRes.value.data.calls || []);
    if (noteRes.status === 'fulfilled') setNotes(noteRes.value.data.notes || []);
    if (emailRes.status === 'fulfilled') setEmails(emailRes.value.data.emails || []);
  }, [leadId]);

  useEffect(() => {
    fetchLead();
    fetchSubData();
  }, [fetchLead, fetchSubData]);

  const handleMoveStage = async (stage) => {
    if (stage === 'registered') {
      setRegisterOpen(true);
      return;
    }
    setMovingStage(stage);
    try {
      await leadsAPI.updateStage(leadId, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      fetchLead();
      fetchSubData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move stage');
    } finally {
      setMovingStage('');
    }
  };

  const handleRegisterComplete = async (data) => {
    setRegisterOpen(false);
    try {
      const res = await leadsAPI.updateStage(leadId, { stage: 'registered', ...data });
      toast.success('Registered as client!');
      fetchLead();
      fetchSubData();
      if (res.data?.client_id) {
        setTimeout(() => {
          if (window.confirm('Go to client profile?')) {
            navigate(`/admin/clients/${res.data.client_id}`);
          }
        }, 300);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!lead) return null;

  const pc = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.warm;
  const PriorityIcon = pc.icon;
  const score = lead.lead_score || 0;
  const nextStages = {
    new_lead: ['in_discussion', 'not_interested'],
    in_discussion: ['prospective', 'not_interested', 'payment_received'],
    not_interested: ['in_discussion'],
    prospective: ['payment_received', 'not_interested'],
    payment_received: ['registered'],
    registered: [],
  }[lead.stage] || [];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare flex flex-col bg-slate-50 overflow-y-auto">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Badge className={STAGE_COLORS[lead.stage]}>
            {STAGE_LABELS[lead.stage]}
          </Badge>
          <Badge className={pc.badge}>
            <PriorityIcon className="w-3 h-3 mr-1" /> {pc.label}
          </Badge>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {nextStages.map((s) => (
            <Button
              key={s}
              size="sm"
              variant="outline"
              onClick={() => handleMoveStage(s)}
              disabled={!!movingStage}
              className={`gap-1.5 text-xs ${s === 'registered' ? 'border-teal-300 text-teal-700 hover:bg-teal-50' : ''}`}
            >
              {movingStage === s ? 'Moving...' : `→ ${STAGE_LABELS[s]}`}
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditOpen(true)}
            className="gap-1.5"
          >
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </Button>
        </div>
      </div>

      {/* Main content: 3 column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — profile */}
        <div className="w-64 flex-shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-4 space-y-4">
          {/* Avatar + name */}
          <div className="text-center pt-2">
            <div className="w-16 h-16 rounded-full bg-indigo-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">
              {getInitials(lead.full_name)}
            </div>
            <h2 className="font-bold text-slate-900 text-lg leading-tight">{lead.full_name}</h2>
            <div className="flex items-center justify-center gap-2 mt-1">
              <Badge className={STAGE_COLORS[lead.stage]} >{STAGE_LABELS[lead.stage]}</Badge>
            </div>
          </div>

          {/* Contact info */}
          <div className="space-y-2 border-t pt-3">
            {lead.email && (
              <div className="flex items-center gap-2 group">
                <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-600 truncate flex-1">{lead.email}</span>
                <button onClick={() => copyToClipboard(lead.email)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                </button>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 group">
                <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-600 truncate flex-1">{lead.phone}</span>
                <button onClick={() => copyToClipboard(lead.phone)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                </button>
              </div>
            )}
            {lead.whatsapp && (
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <a
                  href={`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-green-600 hover:underline truncate"
                >
                  {lead.whatsapp}
                </a>
              </div>
            )}
            {(lead.nationality || lead.country_of_interest) && (
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-xs text-slate-600">
                  {[lead.nationality, lead.country_of_interest].filter(Boolean).join(' → ')}
                </span>
              </div>
            )}
          </div>

          {/* Interest */}
          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Interest</p>
            {lead.product_interest && (
              <div>
                <span className="text-xs text-slate-500">Product: </span>
                <span className="text-xs font-medium text-slate-700">{lead.product_interest}</span>
              </div>
            )}
            {lead.visa_type && (
              <div>
                <span className="text-xs text-slate-500">Visa: </span>
                <span className="text-xs font-medium text-slate-700">{lead.visa_type}</span>
              </div>
            )}
            {lead.quoted_price && (
              <div>
                <span className="text-xs text-slate-500">Quoted: </span>
                <span className="text-xs font-semibold text-slate-800">
                  INR{Number(lead.quoted_price).toLocaleString()}
                </span>
              </div>
            )}
            {lead.discount && (
              <div>
                <span className="text-xs text-slate-500">Discount: </span>
                <span className="text-xs text-emerald-600">- INR{Number(lead.discount).toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Assignment */}
          <div className="border-t pt-3 space-y-1.5">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Assignment</p>
            <div>
              <span className="text-xs text-slate-500">Assigned to: </span>
              <span className="text-xs font-medium text-slate-700">{lead.assigned_to_name || '—'}</span>
            </div>
            <div>
              <span className="text-xs text-slate-500">Source: </span>
              <span className="text-xs text-slate-600">{lead.source?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-xs text-slate-500">Created: </span>
              <span className="text-xs text-slate-600">
                {new Date(lead.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Tags */}
          {lead.tags?.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Tags</p>
              <div className="flex flex-wrap gap-1">
                {lead.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs px-2 py-0.5">{tag}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Score */}
          <div className="border-t pt-3">
            <ScoreBar score={score} />
          </div>

          {/* Follow-up */}
          {lead.follow_up_date && (
            <div className={`border-t pt-3`}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Follow-up</p>
              <div className={`flex items-center gap-1.5 text-xs rounded-lg p-2 ${
                new Date(lead.follow_up_date) < new Date()
                  ? 'bg-rose-50 text-rose-600'
                  : 'bg-sky-50 text-sky-600'
              }`}>
                <Clock className="w-3.5 h-3.5" />
                {new Date(lead.follow_up_date).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Center: Tabs */}
        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue={defaultTab} className="h-full flex flex-col">
            <div className="bg-white border-b border-slate-200 px-6">
              <TabsList className="h-12 bg-transparent border-0 gap-1">
                {[
                  { value: 'overview', label: 'Overview', icon: Activity },
                  { value: 'calls', label: `Calls (${calls.length})`, icon: PhoneCall },
                  { value: 'notes', label: `Notes (${notes.length})`, icon: MessageSquare },
                  { value: 'emails', label: `Emails (${emails.length})`, icon: Mail },
                  { value: 'timeline', label: 'Timeline', icon: Clock },
                ].map(({ value, label, icon: Icon }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="gap-1.5 text-sm data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-700 rounded-none"
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Overview */}
            <TabsContent value="overview" className="flex-1 p-6 space-y-4">
              {/* Pipeline progress */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Pipeline Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-1">
                    {Object.entries(STAGE_LABELS).map(([key, label], i) => {
                      const stages = Object.keys(STAGE_LABELS);
                      const currentIdx = stages.indexOf(lead.stage);
                      const thisIdx = stages.indexOf(key);
                      const isDone = thisIdx < currentIdx;
                      const isCurrent = key === lead.stage;
                      return (
                        <React.Fragment key={key}>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            isCurrent ? 'bg-indigo-600 text-white' :
                            isDone ? 'bg-emerald-100 text-emerald-700' :
                            'bg-slate-100 text-slate-400'
                          }`}>
                            {isDone && <CheckCircle className="w-3 h-3" />}
                            <span className="hidden md:inline">{label}</span>
                            <span className="md:hidden">{i + 1}</span>
                          </div>
                          {i < 5 && (
                            <div className={`flex-1 h-px ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Mail className="w-6 h-6 text-sky-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold">{lead.emails_sent_count || 0}</p>
                    <p className="text-xs text-slate-500">Emails Sent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <PhoneCall className="w-6 h-6 text-green-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold">{lead.calls_count || 0}</p>
                    <p className="text-xs text-slate-500">Calls Logged</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <MessageSquare className="w-6 h-6 text-indigo-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold">{notes.length}</p>
                    <p className="text-xs text-slate-500">Notes</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent activity snippet */}
              {activities.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 divide-y divide-slate-100">
                    {activities.slice(0, 4).map((a) => (
                      <ActivityItem key={a.id} activity={a} />
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Calls */}
            <TabsContent value="calls" className="p-6 space-y-4">
              <CallLogForm leadId={leadId} onSaved={() => { fetchLead(); fetchSubData(); }} />
              <div className="space-y-3">
                {calls.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No calls logged yet</p>
                ) : calls.map((call) => (
                  <div key={call.id} className="bg-white rounded-lg border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-green-100 text-green-700 text-xs">{call.outcome?.replace('_', ' ')}</Badge>
                      <span className="text-xs text-slate-400">{new Date(call.created_at).toLocaleString()}</span>
                    </div>
                    {call.duration_minutes && (
                      <p className="text-xs text-slate-500">Duration: {call.duration_minutes} min</p>
                    )}
                    {call.notes && <p className="text-sm text-slate-700">{call.notes}</p>}
                    {call.next_follow_up && (
                      <p className="text-xs text-sky-600 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Follow-up: {new Date(call.next_follow_up).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Notes */}
            <TabsContent value="notes" className="p-6 space-y-4">
              <NoteForm leadId={leadId} onSaved={fetchSubData} />
              <div className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                ) : notes.map((note) => (
                  <div key={note.id} className="bg-white rounded-lg border p-3">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {note.author_name || 'Unknown'} · {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Emails */}
            <TabsContent value="emails" className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-700">Email History</h3>
                {lead.email && (
                  <Button
                    size="sm"
                    className="bg-sky-600 hover:bg-sky-700 gap-1.5"
                    onClick={() => {
                      const base = window.location.pathname.includes('/sales') ? '/sales' : '/admin';
                      navigate(`${base}/email?compose=1&to=${lead.email}&lead_id=${lead.id}`);
                    }}
                  >
                    <Send className="w-3.5 h-3.5" /> Compose Email
                  </Button>
                )}
              </div>
              {emails.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No emails sent to this lead yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emails.map((email) => (
                    <div key={email.id} className="bg-white rounded-lg border p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-slate-700">{email.subject || '(No subject)'}</p>
                        <Badge variant="outline" className="text-xs">{email.status || 'sent'}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{email.body}</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        {new Date(email.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Timeline */}
            <TabsContent value="timeline" className="p-6">
              <div className="divide-y divide-slate-100">
                {activities.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No activity yet</p>
                ) : activities.map((a) => (
                  <ActivityItem key={a.id} activity={a} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right panel — quick actions */}
        <div className="w-56 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto p-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick Actions</p>
            <div className="space-y-2">
              {lead.email && (
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs"
                  onClick={() => navigate(`/sales/email?compose=1&to=${lead.email}`)}>
                  <Mail className="w-3.5 h-3.5 text-sky-500" /> Send Email
                </Button>
              )}
              {lead.phone && (
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs"
                  onClick={() => window.open(`tel:${lead.phone}`)}>
                  <Phone className="w-3.5 h-3.5 text-green-500" /> Call
                </Button>
              )}
              {lead.whatsapp && (
                <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs"
                  onClick={() => window.open(`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`, '_blank')}>
                  <MessageSquare className="w-3.5 h-3.5 text-green-600" /> WhatsApp
                </Button>
              )}
              <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs"
                onClick={() => setEditOpen(true)}>
                <Edit3 className="w-3.5 h-3.5 text-indigo-500" /> Edit Lead
              </Button>
            </div>
          </div>

          {/* Stage move shortcuts */}
          {nextStages.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Move Stage</p>
              <div className="space-y-1.5">
                {nextStages.map((s) => (
                  <Button
                    key={s}
                    variant="outline"
                    size="sm"
                    className={`w-full justify-start gap-2 text-xs ${s === 'registered' ? 'border-teal-300 text-teal-700 hover:bg-teal-50' : ''}`}
                    onClick={() => handleMoveStage(s)}
                    disabled={!!movingStage}
                  >
                    <ChevronRight className="w-3.5 h-3.5" /> {STAGE_LABELS[s]}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Conversion info */}
          {lead.stage === 'registered' && lead.converted_client_id && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-teal-700 mb-2">
                <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Registered Client
              </p>
              <Button
                size="sm"
                className="w-full text-xs bg-teal-600 hover:bg-teal-700"
                onClick={() => navigate(`/admin/clients/${lead.converted_client_id}`)}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Client
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {editOpen && (
        <LeadEditDrawer
          lead={lead}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchLead(); fetchSubData(); }}
        />
      )}
      {registerOpen && (
        <RegisterLeadModal
          lead={lead}
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          onConfirm={handleRegisterComplete}
        />
      )}
      </div>
    </div>
  );
}
